import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message || "Check your inbox for a reset link.");
      setSubmitted(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16"
      data-testid="forgot-password-page"
    >
      <div className="max-w-md w-full">
        <div className="flex justify-center mb-8">
          <BrandLogo height={64} linkTo="/" darkenGrey />
        </div>
        <div className="ser-card p-8 md:p-10">
          <p className="eyebrow mb-3">Account recovery</p>
          <h1
            className="font-serif-display text-3xl mb-3"
            style={{ color: "var(--ser-primary-dark)" }}
          >
            Forgot your password?
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--ser-text-muted)" }}>
            Enter the email address linked to your account and we&apos;ll send you a
            secure link to choose a new password. The link expires in 30 minutes.
          </p>

          {submitted ? (
            <div
              className="rounded border p-4 mb-4"
              style={{
                background: "var(--ser-surface)",
                borderColor: "var(--ser-sand-dark)",
              }}
              data-testid="forgot-password-submitted"
            >
              <p className="text-sm" style={{ color: "var(--ser-text)" }}>
                If an account exists for <strong>{email}</strong>, a password
                reset link has been sent. Please check your inbox (and spam
                folder). You can close this tab.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <label className="ser-label">Email address</label>
                <input
                  required
                  type="email"
                  className="ser-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-ser-primary w-full"
                data-testid="forgot-password-submit-btn"
              >
                <KeyRound className="w-4 h-4" />
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
          )}

          <div className="flex items-center justify-between mt-6 text-sm">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--ser-primary)" }}
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to sign in
            </Link>
            <Link
              to="/register"
              className="hover:underline"
              style={{ color: "var(--ser-primary)" }}
            >
              Create account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
